import Dialog from "../Dialog";
import { ArrowLeftIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { v4 as uuid } from "uuid";

import { useToast } from "../../contexts/ToastContext";
import { useEffect, useState } from "react";
import { formatNumberToCurrency } from "../../utils";
import API from "../../api/api";
import { useNavigate } from "react-router-dom";
import Button from "../Button";
import { inputFiatAmountRegex, fiatAmountRegex } from "../../utils/regex";
import { MaskedValue, InfoRow, AddressBlock } from "./Atoms";

type Tab = (typeof mainTabs)[number];
type DepositTab = (typeof depositSubTabs)[number];

const mainTabs = ["Deposit", "Withdrawal"] as const;
const depositSubTabs = ["Domestic", "DomesticAch", "International"] as const;
const subTabsTitles = {
  domestic: "Domestic Wires",
  domesticach: "Domestic ACH",
  international: "International",
};

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const achDefaults = {
  accountNumber: "000100108792",
  routingNumber: "111326275",
  receiverAddress: {
    name: "Fortress Trust LLC",
    street1: "10845 Griffith Peak Dr",
    street2: "Suite 200",
    city: "Las Vegas",
    state: "NV",
    postalCode: "89135",
  },
  receiverBankAddress: {
    name: "Cornerstone Capital Bank",
    street1: "1177 West Loop South",
    street2: "Suite 700",
    city: "Houston",
    state: "TX",
    postalCode: "77027",
  },
};

const FiatDepositInstructions = ({
  open,
  setOpen,
  instructions,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  instructions: any;
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [currentTab, setCurrentTab] = useState<Tab>("Deposit");
  const [currentDepositSubTab, setCurrentDepositSubTab] =
    useState<DepositTab>("Domestic");

  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleAchAcct, setIsVisibleAchAcct] = useState(false);
  const [accounts, setBankAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState<null | any>(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>("0");
  const [memo, setMemo] = useState<string>("");
  const withdrawalAmountNumber = Number(withdrawalAmount);
  const { disbursable, accountId } = instructions;
  const { wire, internationalWire } = instructions?.instructions || {};
  const [previewSend, setPreviewSend] = useState(false);
  const [estimatedTransactionDetails, setEstimatedTransactionDetails] =
    useState<{ effectiveDate: string; fees: number } | null>(null);
  const [idempotencyKey] = useState<string>(uuid());

  const { routingNumber, refCode } = wire || {};
  const ach = { ...wire, ...achDefaults };

  // THIS IS REF CODE FOR MASSPAY - they don't like fortress.
  const isNotMasspay = refCode !== "FT7B35185623";
  const isPortageOrganization = routingNumber === "125109064";
  const showCornerstoneInstructions = isNotMasspay && !isPortageOrganization;

  const validateAndSetWithdrawalAmount = (amount: string) => {
    // delete non-numeric chars
    amount = amount.replace(/[^0-9.]/g, "");

    if (inputFiatAmountRegex.test(amount)) {
      setWithdrawalAmount(amount);
    }
  };

  const getAccountDisplayName = (accountId: any) => {
    const account = accounts?.find((account) => account?.id === accountId);
    return `Account: ${account?.description || account?.bankName} ••••${
      account?.last4
    }`;
  };

  const getAccountPaymentRail = (accountId: any) => {
    const account = accounts?.find((account) => account?.id === accountId);
    switch (account?.type) {
      case "financial":
        return "Next Day ACH";
      case "wire":
        return "Same Day Wire";
      default:
        return "";
    }
  };

  const disableSubmit = () => {
    if (
      !selectedAccount ||
      withdrawalAmountNumber === 0 ||
      disbursable < withdrawalAmountNumber ||
      !fiatAmountRegex.test(withdrawalAmount)
    ) {
      return true;
    }
    return false;
  };

  const submitTransfer = async () => {
    setLoading(true);
    // business/:id/transaction
    await API.post(`business/${accountId}/transaction`, {
      paymentAccountId: selectedAccount,
      accountId,
      amount: withdrawalAmountNumber,
      memo,
      idempotencyKey,
    });
    setLoading(false);
    showToast(
      "Transfer successful",
      "",
      <BanknotesIcon height={20} color="green" />,
    );
    // toast success
    // close dialog
    setOpen(false);
    // use selected account and amount
    // make a post request to the api
  };

  useEffect(() => {
    const getBankAccounts = async () => {
      const { data } = await API.get(`business/bankAccounts/${accountId}`);
      setBankAccounts(data);
    };
    getBankAccounts();

    return () => {
      setBankAccounts([]);
    };
  }, [accountId]);

  // get the accounts from the user

  useEffect(() => {
    const getEstimateTransactionDetails = async () => {
      const { data } = await API.post(`business/estimateTransactionDetails`, {
        accountId,
        amount: withdrawalAmountNumber,
        paymentAccountId: selectedAccount,
      });
      if (data) {
        setEstimatedTransactionDetails(data);
      }
    };

    if (selectedAccount && withdrawalAmountNumber > 0) {
      getEstimateTransactionDetails();
    }
  }, [selectedAccount, withdrawalAmountNumber, accountId]);

  const formatedWithdrawalAmount = withdrawalAmountNumber.toLocaleString();

  const depositWire = (
    <>
      <MaskedValue
        label="Account Number"
        value={wire?.accountNumber}
        visible={isVisible}
        toggleVisible={() => setIsVisible((v) => !v)}
        showToast={showToast}
      />

      <InfoRow
        label="Routing Number"
        value={wire?.routingNumber}
        showToast={showToast}
      />

      <div className="flex flex-col items-center justify-center mt-6 w-full">
        <AddressBlock
          title="Account Holder"
          addr={{
            name: showCornerstoneInstructions
              ? "Fortress Trust LLC"
              : wire?.receiverName || wire?.receiverAddress?.name,
            street1: wire?.receiverAddress?.street1,
            street2: wire?.receiverAddress?.street2,
            city: wire?.receiverAddress?.city,
            state: wire?.receiverAddress?.state,
            postalCode: wire?.receiverAddress?.postalCode,
          }}
        />
        <AddressBlock
          title="Bank Details"
          addr={{
            name: wire?.receiverBankName || wire?.receiverBankAddress?.name,
            street1: wire?.receiverBankAddress?.street1,
            street2: wire?.receiverBankAddress?.street2,
            city: wire?.receiverBankAddress?.city,
            state: wire?.receiverBankAddress?.state,
            postalCode: wire?.receiverBankAddress?.postalCode,
          }}
        />
      </div>
    </>
  );

  const depositAch = (
    <>
      <MaskedValue
        label="Account Number"
        value={ach?.accountNumber}
        visible={isVisibleAchAcct}
        toggleVisible={() => setIsVisibleAchAcct((v) => !v)}
        showToast={showToast}
      />

      <InfoRow
        label="Routing Number"
        value={ach?.routingNumber}
        showToast={showToast}
      />

      <div className="flex flex-col items-center justify-center mt-6 w-full">
        <AddressBlock title="Account Holder" addr={ach?.receiverAddress} />
        <AddressBlock title="Bank Details" addr={ach?.receiverBankAddress} />
      </div>
    </>
  );

  const International = (
    <div className="flex flex-col w-full">
      <div className="mt-6 p-4 bg-lavender-50 border border-lavender-200 rounded-md">
        <p className="text-sm text-gray-700">
          <span className="font-semibold">Important:</span>
          When sending an international wire, make sure to include the reference
          code {internationalWire?.refCode || wire?.refCode || "REF"} in the
          memo of your wire. This is required to ensure timely processing.
        </p>
      </div>

      {[
        [
          "Receiving Bank Name",
          internationalWire?.receivingBankName ||
            "The Independent Bankers Bank",
        ],
        [
          "Receiving Bank SWIFT Code",
          internationalWire?.receivingBankSwiftCode || "TIBBUS44",
        ],
        [
          "Beneficiary Financial Institution",
          internationalWire?.beneficiaryFinancialInstitution ||
            "The Roscoe State Bank",
        ],
        [
          "Beneficiary Financial Institution Routing",
          internationalWire?.beneficiaryFinancialInstitutionRouting ||
            "111307485",
        ],
        [
          "Beneficiary",
          internationalWire?.beneficiary || "Fortress Trust, LLC",
        ],
        [
          "Beneficiary Account Number",
          internationalWire?.beneficiaryAccountNumber || "000100108792",
        ],
        [
          "OBI/Notes",
          internationalWire?.obiNotes ||
            "For Further Credit to Cornerstone Capital Bank ABA 111326275",
        ],
      ].map(([label, value]) => (
        <InfoRow
          key={label as string}
          label={label as string}
          value={value as string}
          showToast={showToast}
        />
      ))}
    </div>
  );

  return (
    <Dialog open={open} setOpen={setOpen}>
      <div>
        <button
          className="p-2 absolute right-3 top-3" // Button styling
          onClick={() => {
            setOpen(false);
          }}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => setOpen(false)}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
        {!previewSend && (
          <>
            {/* Mobile view for tabs */}
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">
                Select a tab
              </label>
              {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
              <select
                id="tabs"
                name="tabs"
                className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-lavender-500 focus:outline-none focus:ring-lavender-500 sm:text-sm"
                value={currentTab}
                onChange={(e) => setCurrentTab(e.target.value as Tab)}
              >
                {mainTabs.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            {/* Desktop view for tabs */}
            <div className="hidden sm:block mt-4">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {mainTabs.map((name) => (
                    <span
                      key={name}
                      onClick={() => setCurrentTab(name)}
                      className={classNames(
                        name === currentTab
                          ? "border-lavender-500 text-lavender-800"
                          : "border-transparent text-gray-500 hover:border-lavender-300 hover:text-lavender-700 cursor-pointer",
                        "whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium",
                      )}
                      aria-current={name === currentTab ? "page" : undefined}
                    >
                      {name}
                    </span>
                  ))}
                </nav>
              </div>
            </div>
            {currentTab === "Deposit" && (
              <>
                <div className="mt-4">
                  <div className="border-b border-gray-200">
                    <nav
                      className="-mb-px flex space-x-8"
                      aria-label="Deposit Tabs"
                    >
                      {depositSubTabs.map((name) => (
                        <span
                          key={name}
                          onClick={() => setCurrentDepositSubTab(name)}
                          className={classNames(
                            name === currentDepositSubTab
                              ? "border-lavender-500 text-lavender-800"
                              : "border-transparent text-gray-500 hover:border-lavender-300 hover:text-lavender-700 cursor-pointer",
                            "whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium",
                          )}
                        >
                          {subTabsTitles[name.toLowerCase()] || name}
                        </span>
                      ))}
                    </nav>
                  </div>
                </div>
                {["Domestic", "DomesticAch"].includes(currentDepositSubTab) && (
                  <p className="mt-2 text-sm text-gray-500 text-center sm:text-left">
                    {isNotMasspay
                      ? `The following account can be used to send Wires or ACH. Important: Be sure to include ${refCode} in the memo of your wire. This is required to ensure timely processing.`
                      : "The following account can be used for ACH and Wire"}
                  </p>
                )}
                {currentDepositSubTab === "International" && (
                  <p className="mt-2 text-sm text-gray-500 text-center sm:text-left">
                    For international wires, please use the following
                    intermediary and beneficiary bank details. Be sure to
                    include all information to ensure proper processing..
                  </p>
                )}

                <div className="flex flex-col justify-center items-center mt-4 space-y-2">
                  {currentDepositSubTab === "Domestic" && depositWire}
                  {currentDepositSubTab === "DomesticAch" && depositAch}
                  {currentDepositSubTab === "International" && International}
                </div>
              </>
            )}
            {currentTab === "Withdrawal" && (
              <div className="flex flex-col justify-center items-center mt-4 space-y-2">
                <div className="flex w-full items-center justify-between py-4">
                  <div className="flex flex-col mt-2 w-full">
                    <label
                      htmlFor="bank-address-city"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Amount
                    </label>
                    <div className="mt-2">
                      <div className="relative rounded-md shadow-sm">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="text"
                          name="price"
                          id="price"
                          value={withdrawalAmount}
                          onChange={(e) =>
                            validateAndSetWithdrawalAmount(e.target.value)
                          }
                          className="block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-lavender-600 sm:text-sm sm:leading-6"
                          placeholder="0.00"
                          aria-describedby="price-currency"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <button
                            onClick={() =>
                              validateAndSetWithdrawalAmount(
                                String(disbursable),
                              )
                            }
                            className="hover:bg-gray-200 rounded cursor-pointer"
                            aria-label={"Set max available"}
                          >
                            <span
                              className="text-gray-500 sm:text-sm"
                              id="set-max-available"
                            >
                              MAX
                            </span>
                          </button>
                        </div>
                      </div>
                      <p
                        className={`mt-2 text-sm ${
                          withdrawalAmountNumber <= disbursable
                            ? "text-gray-500"
                            : "text-red-500"
                        }`}
                        id="email-description"
                      >
                        {withdrawalAmountNumber <= disbursable
                          ? `$${formatNumberToCurrency(disbursable)} available`
                          : `Insufficient funds $${formatNumberToCurrency(
                              disbursable,
                            )} available`}
                      </p>
                    </div>
                    <div className="mt-4">
                      <label
                        htmlFor="account"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Destination Account
                      </label>
                      <select
                        id="account"
                        name="account"
                        className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-lavender-600 sm:text-sm sm:leading-6"
                        defaultValue=""
                        value={selectedAccount}
                        onChange={(e) => {
                          if (e.target.value === "add") {
                            navigate("/connected-accounts#add-bank-account");
                          }
                          setMemo("");
                          setSelectedAccount(e.target.value);
                        }}
                      >
                        <option value="">Select an account</option>
                        {accounts?.map((account, idx) => (
                          <option
                            value={account.id}
                            key={`${idx}-${account.id}`}
                          >
                            {getAccountDisplayName(account.id)}
                          </option>
                        ))}
                        <option value="add">Add a payment method</option>
                      </select>
                    </div>
                    {accounts?.find((account) => account.id === selectedAccount)
                      ?.type === "wire" && (
                      <>
                        <div className="mt-4">
                          <label
                            htmlFor="account"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Memo
                          </label>
                          <input
                            type="text"
                            name="memo"
                            id="memo"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            maxLength={140}
                            className="mt-2 block w-full rounded-md border-0 py-1.5 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-lavender-600 sm:text-sm sm:leading-6"
                            placeholder="Invoice 123"
                            aria-describedby="wire-memo"
                          />
                        </div>
                      </>
                    )}
                    <div className="mt-12 w-full">
                      <Button
                        text="Review"
                        loading={false}
                        className="w-full justify-center"
                        onSubmit={() => {
                          setPreviewSend(true);
                        }}
                        disabled={disableSubmit()}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {previewSend && (
          <div className="flex flex-col align-start">
            <div className="flex justify-center items-center w-full">
              <button
                onClick={() => setPreviewSend(false)}
                className="flex items-center px-2 py-1 text-gray-500 hover:text-gray-700 absolute left-5 top-5"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-1" />
              </button>
              <span className="text-2xl">
                Transfer ${formatedWithdrawalAmount} USD
              </span>
            </div>

            <div className="mt-4 flex flex-row justify-between">
              <span className="font-semibold">Transfer To</span>
              <span className="text-right">
                {getAccountDisplayName(selectedAccount)}
              </span>
            </div>

            <div className="mt-4 flex flex-row justify-between">
              <span className="font-semibold">Payment Rail</span>
              <span className="text-right">
                {getAccountPaymentRail(selectedAccount)}
              </span>
            </div>
            {memo && (
              <div className="mt-4 flex flex-row justify-between">
                <span className="font-semibold">Memo</span>
                <span className="text-right">{memo}</span>
              </div>
            )}

            <div className="mt-4 flex flex-row justify-between">
              <span className="font-semibold">Estimated Delivery</span>
              <span className="text-right">
                {estimatedTransactionDetails?.effectiveDate}
              </span>
            </div>

            <div className="mt-4 flex flex-row justify-between">
              <span className="font-semibold">Payment Fees</span>
              <span className="text-right">
                ${estimatedTransactionDetails?.fees}
              </span>
            </div>

            <Button
              text="Send Now"
              loading={loading}
              className="w-full justify-center mt-4"
              disabled={loading}
              onSubmit={() => {
                submitTransfer();
              }}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default FiatDepositInstructions;
